/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useImperativeHandle, useMemo, useState } from "react";

// Components
import { useColumnDefinition } from "./hooks/useColumnDefinition";
import { useColumnFilter } from "./hooks/useColumnFilter";
import { useUiSetting } from "./hooks/useUiSetting";
import { ActionEnum } from "./ActionEnum";
import SntConfigColumns from "./SntConfigColumns";
import { SntTableViewCard } from "./SntTableViewCard";
import DataUtils from "@/utils/DataUtils";
import type { SntAdvancedSearchTableViewCardProps } from "./types";
import type { ColumnFilterSettings } from "@/types/ColumnTypes";

const SntAdvancedSearchTableViewCard = ({
  allColumns,
  extraCols,
  preventCols,
  visibleCols,
  disabled = false,
  onChange,
  isFloatingFooter,
  dataOptions,
  initialPageSize = 25,
  defaultSortBy,
  shared,
  orgId,
  useOrgInSidebar,
  categoryId,
  addFilter,
  noDataText,
  includeDynamicColumn = false,
  pageName,
  isDisabbledGroup = false,
  isShowDecoratorColor,
  isShowSelectPageSize = true,
  isShowPaging = true,
  isShowConfigColumn = true,
  onRenderConfig,
  fixedSetting,
  isDynamicDashBoard,
  height,
  onCountClicked,
  onExtraColumnActionClicked,
  isDisabledCopyData,
  isDisabbledAddFilter,
  restrictedFilters,
  onFilterClicked,
  filterMap,
  skipLoadSettings = false,
  externalFilterVisibleKeys,
}: SntAdvancedSearchTableViewCardProps) => {
  console.log(
    "[SntAdvancedSearchTableViewCard] Received includeDynamicColumn prop:",
    includeDynamicColumn
  );
  const [isShowConfigDialog, setShowConfigDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);

  // Column definition
  const { allColumnDefs: columnsDef, getNewCols } = useColumnDefinition(
    dataOptions.queryObjectType,
    dataOptions.categories,
    preventCols,
    {
      originalCols: allColumns,
      extraCols: extraCols,
      includeDynamicColumn: includeDynamicColumn,
    }
  );
  const customPageName = skipLoadSettings
    ? null
    : pageName || dataOptions.pageName;
  const { setting, updateSetting } = useUiSetting(
    customPageName,
    fixedSetting || null
  );

  const dataFilter = useColumnFilter(
    columnsDef ?? null,
    {
      defaultVisibleColumnKeys: visibleCols,
      defaultPageSize: initialPageSize,
      onCountClicked: onCountClicked,
      defaultSortBy: defaultSortBy,
    },
    setting as ColumnFilterSettings
  );
  const {
    filter,
    setPageCount,
    getPageData,
    setVisibleColumnKeys,
    setColumnsSetting,
    resetDefault,
    setTotalItems,
    refresh,
    loadData,
    setPaging,
  } = dataFilter;

  const _getDataRequest = () => {
    const { start, limit, sort } = getPageData?.() ?? { start: 0, limit: 10, sort: '[]' };
    const params = _getParamFormat(
      start,
      limit,
      sort,
      dataOptions.optionsSettings && dataOptions.optionsSettings.query,
      dataOptions.queryObjectType,
      dataOptions.optionsSettings && dataOptions.optionsSettings.searchQueryType
    );

    if (useOrgInSidebar && orgId) {
      _addOrgInSidebarToFilter(params);
    }

    addFilter?.(params);

    return { params, dataOptions };
  };

  const _addOrgInSidebarToFilter = (params: any) => {
    // remove old
    params.query = params.query.filter(function (param: any) {
      return param.filterKey !== "organizations";
    });

    // add new
    params.query.push({
      categoryId: categoryId || "userInfo",
      filterKey: "organizations",
      filterType: "MULTI_SELECT_AUTO_COMPLETE",
      searchType: "STATIC",
      notFilter: false,
      filterValue: {
        includeEmpty: false,
        includeNoEmpty: false,
        selectedValues: [orgId],
      },
    });
  };

  const _getParamFormat = (
    start: any,
    limit: any,
    sort: any,
    queries: any,
    queryObjectType: any,
    searchQueryType: any
  ) => {
    const { requestedTableColumns: currentColumns } = getPageData?.() ?? { requestedTableColumns: [] };
    const parsedSort = JSON.parse(sort);
    const orderData = [parsedSort[0].property, parsedSort[0].direction];

    return {
      maxResults: limit,
      sort: sort || "[]",
      query: queries || [],
      queryObjectType: queryObjectType,
      // currentColumns can a list of string, or list of group object
      requestedTableColumns: (currentColumns || []).map((obj: any) => {
        if (typeof obj === "string") {
          return {
            columnKey: obj,
            order: obj === orderData[0] ? orderData[1] : null,
          };
        }
        return {
          ...obj,
        };
      }),
      searchQueryType: searchQueryType || "BASIC",
      startIndex: start,
    };
  };

  const _getDataCallBack = (params: any, dataOptions: any, data: any) => {
    const { start, limit, groups } = getPageData?.() ?? { start: 0, limit: 10, groups: [] };

    const parsedSort = JSON.parse(params.sort);
    const orderData = [parsedSort[0].property, parsedSort[0].direction];

    return [
      {
        limit: limit,
        orderData: orderData,
        query: params.query,
        searchQueryType: params.searchQueryType,
        start: start,
        tableSettings: {
          columnVisibleInOrder: params.requestedTableColumns.map(
            (c: any) => c.columnKey
          ),
          groups: groups,
          orderData: orderData,
          pageName: customPageName,
          pageSize: limit,
          switchMode: "TABLE", // unknow
        },
      },
      { countByFilter: data.countByFilter, countAll: data.countAll },
    ];
  };

  useEffect(() => {
    if (!filter || hasLoadedInitialData) return;

    setHasLoadedInitialData(true);
    loadData();
    if (dataOptions.action && dataOptions.action.type) {
      if (dataOptions.action.type === ActionEnum.LOAD_FILTER) {
        if (
          dataOptions.optionsSettings &&
          dataOptions.optionsSettings.tableSettings
        ) {
          const columnKeys =
            dataOptions.optionsSettings.tableSettings.columnVisibleInOrder ||
            [];
          const orderData = dataOptions.optionsSettings.tableSettings
            .orderData || [columnKeys[0], "DESC"];

          const groups = dataOptions.optionsSettings.tableSettings.groups || [];
          let groupKey = null;
          let bucketType = null;
          let bucketConfig = null;
          let metrics = [];
          if (groups.length > 0) {
            groupKey = groups[0].aggregateColumn;
            bucketType = groups[0].bucketType;
            bucketConfig = groups[0].bucketConfig;
            metrics = groups
              .filter((metricObj: any) => metricObj.aggregationType)
              .map(
                (metricObj: any) =>
                  metricObj.aggregateColumn +
                  "." +
                  metricObj.aggregationType.toLowerCase()
              );
          }
          setColumnsSetting(
            columnKeys,
            { key: orderData[0], desc: orderData[1] === "DESC" },
            groupKey,
            bucketType,
            bucketConfig,
            metrics
          );
          setVisibleColumnKeys(
            dataOptions.optionsSettings.tableSettings.columnVisibleInOrder || []
          );
        }
      } else if (dataOptions.action.type === ActionEnum.CHANGE_MORE_FILTER) {
        const isGrouped = filter.columns.find((f) => f.aggregationType);
        if (!isGrouped) {
          const newCols = getNewCols?.(dataOptions) ?? [];
          setVisibleColumnKeys([
            ...filter.columns.map((item) => item.columnKey),
            ...newCols,
          ]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, dataOptions]);

  useEffect(() => {
    if (filter && filter.sortBy) {
      updateSetting(filter as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (!filter || !dataOptions) return;
    const timeout = setTimeout(() => {
      const request = _getDataRequest();
      onChange?.(request);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, dataOptions]);

  const [presentFilters, setPresentFilters] = useState<string[]>([]);

  useEffect(() => {
    const query = dataOptions?.optionsSettings?.query || [];

    const presentWithValueKey: string[] = [];
    query.forEach((item: any) => {
      const { filterValue, filterType } = item;

      if (item.notFilter) {
        presentWithValueKey.push(item.filterKey);
      } else if (filterValue) {
        if (filterType === "MULTI_SELECT_CHECKBOX") {
          const selectedValues = filterValue.selectedValues || [];
          const operator = filterValue.operator;
          if (selectedValues.length === 0 && (!operator || operator === "IN"))
            return;
          presentWithValueKey.push(item.filterKey);
        } else if (filterType === "STRING") {
          const { includeEmpty, includeNoEmpty, searchText } = filterValue;
          if (!searchText && !includeEmpty && !includeNoEmpty) return;
          presentWithValueKey.push(item.filterKey);
        } else if (filterType === "SPATIAL") {
          const { includeEmpty, includeNoEmpty } = filterValue;
          if (!includeEmpty && !includeNoEmpty) return;
          presentWithValueKey.push(item.filterKey);
        } else if (filterType === "MULTI_STRING") {
          const { searchText } = filterValue;
          if (!searchText) return;
          presentWithValueKey.push(item.filterKey);
        } else if (filterType === "BOOLEAN") {
          const { value, naOnly } = filterValue;
          if (value === null && !naOnly) return;
          presentWithValueKey.push(item.filterKey);
        } else if (filterType === "NUMERIC" || filterType === "DATE") {
          presentWithValueKey.push(item.filterKey);
        }
      }
    });
    setPresentFilters(presentWithValueKey);
  }, [dataOptions]);

  const combinedFilterVisibleKeys = useMemo(
    () => [...presentFilters, ...(externalFilterVisibleKeys || [])],
    [presentFilters, externalFilterVisibleKeys]
  );

  const [data, setData] = useState([]);

  // Expose available functions for parents
  useImperativeHandle(shared as any, () => ({
    refresh: () => {
      refresh?.();
    },
    resetDefault: () => {
      resetDefault?.();
      refresh?.();
    },
    setLoading: (value: any) => {
      setLoading(value);
    },
    // setData: (data) => {
    //   setData(data);
    // },
    // setPageCount: (pageCount) => {
    //   setPageCount(Math.ceil(pageCount / filter.pageSize));
    // },
    // setTotalItems: (totalItem) => {
    //   setTotalItems(totalItem);
    // },
    updateDataTable: ({
      data,
      convertData,
      compactData,
      pageCount,
      totalItem,
    }: {
      data: any;
      convertData: any;
      compactData: any;
      pageCount: any;
      totalItem: any;
    }) => {
      const dataList = DataUtils.convertData({ data, convertData, compactData });
      setData(dataList);
      if (filter) {
        const pageNrOf = Math.ceil(pageCount / filter.pageSize);
        setPageCount(pageNrOf);
        setTotalItems(totalItem);
        // Only reset pagination if we're not in a count loading state (isCountLoading indicates async count update)
        if (filter.pageIndex >= pageNrOf && !totalItem?.isCountLoading) {
          setPaging(0, filter.pageSize);
        }
      }
    },
    getDataCallBack: (params: any, dataOptions: any, data: any) => {
      return _getDataCallBack(params, dataOptions, data);
    },
  }));

  if (!dataFilter.filter) return <></>;

  let onConfigColumn = null;
  if (isShowConfigColumn) {
    onConfigColumn = () => {
      setShowConfigDialog(true);
    };
  }

  return (
    <div>
      {isShowConfigDialog && (
        <SntConfigColumns
          dataFilter={{...dataFilter, allColumns: dataFilter.allColumns || [], defaultVisibleColumnKeys: dataFilter.defaultVisibleColumnKeys || []} as any}
          isShowConfigDialog={true}
          onDone={() => setShowConfigDialog(false)}
          onCancel={() => setShowConfigDialog(false)}
          isDisabbledGroup={isDisabbledGroup}
          optionsSettings={dataOptions.optionsSettings}
          defaultSortBy={defaultSortBy}
        />
      )}
      <SntTableViewCard
        dataFilter={dataFilter}
        data={data}
        onConfigColumn={onConfigColumn}
        loading={loading}
        noDataText={noDataText}
        isFloatingFooter={isFloatingFooter}
        isDisabbledGroup={isDisabbledGroup}
        isShowDecoratorColor={isShowDecoratorColor}
        isShowSelectPageSize={isShowSelectPageSize}
        isShowPaging={isShowPaging}
        onRenderConfig={onRenderConfig}
        isDynamicDashBoard={isDynamicDashBoard}
        height={height}
        onExtraColumnActionClicked={onExtraColumnActionClicked}
        isDisabledCopyData={isDisabledCopyData}
        isDisabbledAddFilter={isDisabbledAddFilter}
        restrictedFilters={restrictedFilters}
        onFilterClicked={onFilterClicked}
        filterMap={filterMap}
        filterVisibleKeys={combinedFilterVisibleKeys}
      />
    </div>
  );
};

export default SntAdvancedSearchTableViewCard;
